"use client";

import Link from "next/link";
import { Cpu, Gauge, Layers3 } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import {
  Code,
  IconArrowRight,
  IconArrowUpRight,
  IconBolt,
  IconCpu,
  IconGlobe,
  IconHeadset,
  IconSliders,
  type IconProps,
} from "@/app/_components/icons";
import { FAQAccordion } from "@/components/photon/faq-accordion";
import { PerfAnimation } from "@/components/photon/perf-animation";

const PHOTON_FACTS = [
  {
    value: "~2x",
    label: "faster than vLLM on comparable Moondream workloads",
  },
  {
    value: "Realtime inference",
    label: "34ms end-to-end inference on a B200.",
  },
  {
    value: "8+",
    label: "hardware tiers from Jetson devices to B200 servers",
  },
] as const;

const ENGINE_STEPS = [
  {
    Icon: IconBolt,
    title: "Fast by design",
    body: "Photon pairs optimized scheduling, native image processing, and purpose-built CUDA kernels for Moondream instead of a generic model-serving path.",
  },
  {
    Icon: IconSliders,
    title: "Production serving features",
    body: "Streaming, automatic batching, prefix caching, and paged KV cache are built into the engine so teams can serve Moondream under real load.",
  },
  {
    Icon: IconGlobe,
    title: "One API everywhere",
    body: "Run the same Moondream skills on edge devices, local workstations, on-prem servers, or your own cloud infrastructure.",
  },
] as const;

const HARDWARE_TIERS = [
  {
    name: "Server",
    detail: "B200, H100, A100, L40S, A10, L4",
    body: "Cloud inference, batch jobs, and high-throughput APIs.",
  },
  {
    name: "Desktop",
    detail: "RTX 4090, 4080, 3090, 3060",
    body: "Local development, prototyping, and on-prem workloads.",
  },
  {
    name: "Edge",
    detail: "Jetson AGX Orin, Orin NX, Orin Nano",
    body: "Cameras, robots, drones, and embedded systems.",
  },
] as const;

const FEATURE_HIGHLIGHTS = [
  "Real-time token streaming for query and caption tasks.",
  "Captioning, visual Q&A, pointing, object detection, and segmentation.",
  "Native support for Moondream fine-tunes loaded directly by ID.",
  "Automatic batching without adding per-request latency.",
  "Prefix caching for repeated prompts and images.",
  "Paged KV cache for many concurrent requests.",
] as const;

const PhotonFactCard = ({
  value,
  label,
}: {
  value: string;
  label: string;
}) => (
  <div className="model-card model-card-cyan">
    <div className="model-card-head">
      <h3 className="tabular">{value}</h3>
      <span>Photon</span>
    </div>
    <p>{label}</p>
  </div>
);

const EngineStep = ({
  Icon,
  title,
  body,
}: {
  Icon: (props: IconProps) => ReactNode;
  title: string;
  body: string;
}) => (
  <div className="lens-feat">
    <div className="lens-icon">
      <Icon size={20} />
    </div>
    <div>
      <div className="t-heading-sm">{title}</div>
      <p
        className="t-body-md"
        style={{ color: "var(--text-secondary)", marginTop: 4 }}
      >
        {body}
      </p>
    </div>
  </div>
);

const InstallSnippet = () => (
  <div className="hero-install">
    <span className="hero-install-prompt mono">$</span>
    <span className="hero-install-cmd mono">pip install moondream</span>
    <a
      href="https://docs.moondream.ai/running-locally"
      className="hero-install-copy"
      aria-label="Open Photon quickstart"
    >
      <IconArrowUpRight size={14} />
    </a>
  </div>
);

const HeroVisual = () => (
  <div style={{ padding: 24, overflow: "hidden" }}>
    <div className="hero-eyebrows">
      <span className="chip chip-accent">
        <span className="dot" /> Local inference
      </span>
      <span className="chip">Moondream runtime</span>
    </div>

    <div style={{ marginTop: 20 }}>
      <PerfAnimation />
      <p
        className="t-caption mono"
        style={{ color: "var(--text-tertiary)", marginTop: 8, textAlign: "right" }}
      >
        Moondream 2 ·{" "}
        <a
          href="https://github.com/m87-labs/kestrel/blob/main/PERFORMANCE.md"
          target="_blank"
          rel="noopener noreferrer"
          className="link"
        >
          full benchmarks
        </a>
      </p>
    </div>

    <div className="concerns" style={{ marginTop: 16, marginBottom: 0 }}>
      <div className="concern" style={{ padding: 18 }}>
        <div className="concern-icon" style={{ marginBottom: 12 }}>
          <Gauge size={20} />
        </div>
        <div className="t-heading-sm">Low latency</div>
        <p className="t-body-sm" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
          Tuned for real-time visual decisions.
        </p>
      </div>
      <div className="concern" style={{ padding: 18 }}>
        <div className="concern-icon" style={{ marginBottom: 12 }}>
          <Cpu size={20} />
        </div>
        <div className="t-heading-sm">Local</div>
        <p className="t-body-sm" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
          Inference runs on your hardware.
        </p>
      </div>
      <div className="concern" style={{ padding: 18 }}>
        <div className="concern-icon" style={{ marginBottom: 12 }}>
          <Layers3 size={20} />
        </div>
        <div className="t-heading-sm">Complete</div>
        <p className="t-body-sm" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
          Built for every Moondream skill.
        </p>
      </div>
    </div>
  </div>
);

const QuickstartCode = () => (
  <Code
    lang="python"
    tokens={[
      ["k", "import"],
      " moondream ",
      ["k", "as"],
      " md\n",
      ["k", "from"],
      " PIL ",
      ["k", "import"],
      " Image\n\n",
      "model = md.",
      ["f", "vl"],
      "(api_key=",
      ["s", '"YOUR_API_KEY"'],
      ", local=",
      ["k", "True"],
      ")\n",
      "image = Image.",
      ["f", "open"],
      "(",
      ["s", '"photo.jpg"'],
      ")\n\n",
      ["f", "print"],
      "(model.",
      ["f", "caption"],
      "(image)[",
      ["s", '"caption"'],
      "])\n",
      ["f", "print"],
      "(model.",
      ["f", "query"],
      "(image, ",
      ["s", '"What is happening?"'],
      ")[",
      ["s", '"answer"'],
      "])\n",
    ]}
  />
);

export function PhotonPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");

  return (
    <div className="md-landing">
      <Nav theme={theme} onToggleTheme={toggleTheme} />

      <main>
        <section className="hero section">
          <div className="container hero-grid">
            <div className="hero-col-text">
              <div className="hero-eyebrows">
                <span className="chip chip-accent">
                  <span className="dot" /> Photon
                </span>
                <span className="chip">Official Moondream inference engine</span>
              </div>

              <h1 className="hero-headline t-display-xl" style={{ marginTop: 28 }}>
                Run Moondream fast, wherever production lives.
              </h1>
              <p
                className="hero-sub t-body-lg"
                style={{
                  color: "var(--text-secondary)",
                  maxWidth: 560,
                  marginTop: 24,
                }}
              >
                Photon is the high-performance runtime for Moondream models on
                edge devices, desktops, servers, and private clouds. Same model
                skills, lower latency, and no generic inference stack in the
                middle.
              </p>

              <div className="hero-ctas" style={{ marginTop: 36 }}>
                <a
                  href="https://docs.moondream.ai/running-locally"
                  target="_blank"
                  rel="noreferrer"
                  className="btn btn-primary"
                >
                  Get started <IconArrowRight size={14} />
                </a>
                <Link href="/cloud" className="btn btn-secondary">
                  Prefer hosted? See Cloud <IconArrowRight size={14} />
                </Link>
              </div>
            </div>

            <div className="hero-col-demo">
              <HeroVisual />
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 48 }}>
          <div className="container">
            <div className="model-card-grid">
              {PHOTON_FACTS.map((fact) => (
                <PhotonFactCard key={fact.value} {...fact} />
              ))}
            </div>
          </div>
        </section>

        <>
          <section className="section" id="performance">
            <div className="container">
              <div className="lens-grid">
                <div className="lens-rail">
                  {ENGINE_STEPS.map((step) => (
                    <EngineStep key={step.title} {...step} />
                  ))}
                </div>
                <div className="honest-card" style={{ marginTop: 0 }}>
                  <div className="t-eyebrow" style={{ color: "var(--text-tertiary)" }}>
                    Why Photon
                  </div>
                  <h3 className="t-heading-md" style={{ marginTop: 10 }}>
                    Generic inference engines leave Moondream performance on the table.
                  </h3>
                  <p
                    className="t-body-md"
                    style={{ color: "var(--text-secondary)", marginTop: 10 }}
                  >
                    Photon only has to serve Moondream, so the runtime can make
                    model-specific decisions about scheduling, memory, image
                    preprocessing, and streaming behavior.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="section" id="compatibility">
            <div className="container">
              <div>
                <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                  Compatibility
                </div>
                <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
                  NVIDIA GPUs from embedded edge to multi-GPU servers.
                </h2>
                <p
                  className="t-body-lg"
                  style={{
                    color: "var(--text-secondary)",
                    marginTop: 14,
                    maxWidth: 640,
                  }}
                >
                  Deploy the same engine on Jetson devices, desktop cards, and
                  server-class GPUs. Your deployment story can move without
                  changing the model API.
                </p>
              </div>

              <div className="products-grid">
                {HARDWARE_TIERS.map((tier, index) => (
                  <div className="product-card" key={tier.name}>
                    <div className="product-head">
                      <div className="product-icon">
                        <IconCpu size={20} />
                      </div>
                      <span className="mono product-num">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="t-heading-md" style={{ marginTop: 24 }}>
                      {tier.name}
                    </div>
                    <p
                      className="t-body-md"
                      style={{ color: "var(--text-secondary)", marginTop: 8 }}
                    >
                      {tier.body}
                    </p>
                    <div className="product-cta mono">{tier.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="section" id="quickstart">
            <div className="container">
              <div className="try-intro-grid">
                <div className="try-intro-heading">
                  <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                    Quickstart
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
                    Install the SDK and run locally in a few lines.
                  </h2>
                </div>
                <div>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 0,
                      maxWidth: 640,
                    }}
                  >
                    The API key accesses your fine-tunes and billing telemetry.
                    Your images, prompts, and inference stay on your hardware.
                  </p>
                  <div className="step-actions">
                    <InstallSnippet />
                  </div>
                </div>
                <div className="try-intro-code">
                  <QuickstartCode />
                </div>
              </div>
            </div>
          </section>

          <section className="section" id="features">
            <div className="container">
              <div>
                <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                  Features
                </div>
                <h2 className="t-heading-xl" style={{ marginTop: 10, maxWidth: 720 }}>
                  Everything you need to serve Moondream in production.
                </h2>
              </div>

              <div className="model-card-grid photon-feature-grid">
                {FEATURE_HIGHLIGHTS.map((feature, index) => (
                  <div className="model-card" key={feature}>
                    <div className="model-card-head">
                      <h3>{feature}</h3>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>

        <section className="section" id="faq">
          <div className="container">
            <div className="lens-grid">
              <div>
                <h2 className="t-heading-xl" style={{ maxWidth: 720 }}>
                  Frequently asked questions
                </h2>
              </div>
              <FAQAccordion hideHeader />
            </div>
          </div>
        </section>

        <section className="section final-cta">
          <div className="container">
            <div className="final-card card">
              <div className="final-inner">
                <div>
                  <div className="t-eyebrow" style={{ color: "var(--accent-text)" }}>
                    Run locally
                  </div>
                  <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
                    Put Photon under your Moondream workload.
                  </h2>
                  <p
                    className="t-body-lg"
                    style={{
                      color: "var(--text-secondary)",
                      marginTop: 12,
                      maxWidth: 620,
                    }}
                  >
                    Start with the SDK, keep inference on your hardware, and
                    bring in our team when you need dedicated deployment help.
                  </p>
                </div>
                <div className="final-ctas">
                  <a
                    href="https://docs.moondream.ai/running-locally"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary"
                  >
                    Read docs <IconArrowUpRight size={14} />
                  </a>
                  <a href="mailto:sales@moondream.ai" className="btn btn-secondary">
                    Talk to us <IconHeadset size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
